import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { getAuthenticatedHomePath } from "@/lib/authenticated-home";
import { toast } from "sonner";
import { pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () =>
    pageHead({
      title: m.reset_meta_title(),
      description: m.reset_meta_description(),
      path: "/reset-password",
      noindex: true,
    }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Supabase recovery flow drops a session via URL hash; the client picks it up automatically.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        setHasSession(true);
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      setHasSession(!!data.session);
      setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error(m.reset_toast_mismatch());
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success(m.reset_toast_updated());
      const to = await getAuthenticatedHomePath();
      navigate({ to, replace: true });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-ink page-band-pad">
      <div className="container-page">
        <div className="mx-auto max-w-md border border-white/10 bg-black/40 p-8">
          <h1 className="font-display text-3xl tracking-[0.08em] text-foreground">
            {m.reset_heading()}
          </h1>

          {!ready && <p className="mt-4 text-sm text-foreground/60">{m.common_loading()}</p>}

          {ready && !hasSession && (
            <div className="mt-4 space-y-3 text-sm text-foreground/70">
              <p>{m.reset_invalid_link()}</p>
              <Link to="/auth" className="inline-block text-ember hover:underline">
                {m.reset_request_new()}
              </Link>
            </div>
          )}

          {ready && hasSession && (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div>
                <Label htmlFor="pw">{m.reset_label_new()}</Label>
                <Input
                  id="pw"
                  type="password"
                  autoComplete="new-password"
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="pw2">{m.reset_label_confirm()}</Label>
                <Input
                  id="pw2"
                  type="password"
                  autoComplete="new-password"
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-ember-fill text-primary-foreground hover:bg-ember-fill/90"
              >
                {m.reset_btn_update()}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
